/**
 * Client-side Google Sheets service
 * Fetches package data from the API endpoint
 */
export class PackageDataService {
  constructor() {
    this.cache = null;
    this.cacheExpiry = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Fetch package data from API
   */
  async fetchPackageData() {
    // Check cache first
    if (this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      console.log('📦 Package Service: Using cached package data (cache still valid)');
      return this.cache;
    }

    try {
      console.log('📦 Package Service: Fetching package data from API endpoint...');
      
      // Try API first (works in production/vercel dev)
      const response = await fetch('/api/packages');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache the data
      this.cache = data;
      this.cacheExpiry = Date.now() + this.cacheDuration;
      
      console.log('✅ Package Service: Package data fetched and cached successfully from API');
      console.log('📊 Package Service: API data contains:', {
        '3star': data['slider-3star']?.length || 0,
        '4star': data['slider-4star']?.length || 0,
        '5star': data['slider-5star']?.length || 0
      });
      return data;
    } catch (error) {
      console.error('❌ Package Service: Error fetching package data from API:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      // Log specific error types
      if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
        console.error('🌐 Package Service: Network error - unable to reach API endpoint');
      } else if (error.message?.includes('404')) {
        console.error('🔍 Package Service: API endpoint not found (404)');
      } else if (error.message?.includes('500')) {
        console.error('💥 Package Service: Server error (500) - API endpoint failed');
      } else if (error.message?.includes('503')) {
        console.error('⏰ Package Service: Service unavailable (503) - API temporarily down');
      } else {
        console.error('🔍 Package Service: Unknown API error occurred');
      }
      
      // Re-throw error - let frontend handle "no packages available" UI
      throw error;
    }
  }

  /**
   * Format price for display
   */
  formatPrice(price) {
    if (typeof price === 'number') {
      return `£${price.toLocaleString()}`;
    }
    return price;
  }

  /**
   * Format date for display
   */
  formatDate(dateString) {
    if (!dateString) return '';
    try {
      // Normalize various Unicode dash characters to regular hyphens
      const normalizedDate = dateString
        .replace(/[\u2010-\u2015\u2212]/g, '-') // Various Unicode dashes
        .replace(/\u2013/g, '-') // En dash
        .replace(/\u2014/g, '-') // Em dash
        .replace(/‑/g, '-') // Non-breaking hyphen
        .trim();
      
      const date = new Date(normalizedDate);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString, 'normalized to:', normalizedDate);
        return dateString;
      }
      
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'dateString:', dateString);
      return dateString;
    }
  }

  /**
   * Format time for display
   */
  formatTime(timeString) {
    if (!timeString) return '';
    try {
      // Handle various time formats
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return timeString;
    }
  }

  /**
   * Clear cache (useful for testing or forced refresh)
   */
  clearCache() {
    this.cache = null;
    this.cacheExpiry = null;
  }
}

// Export singleton instance
export const packageDataService = new PackageDataService();
